import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { RaisePoInputs } from './Inputs';
import { RaisePOButtons } from './SubmitButtons';
import Form from '../../common/Form';
import { createPo, getApprovalTemplates } from '../../actions/payments';
import { getServiceRequestById } from '../../actions/service_request';
import { canManageOtherOrgs } from '../../utils/access';
import { actualValueForDropdown, debounce, validateStringValue } from '../../utils/common';
import { closeModalAction, openCustomModal } from '../../actions/modal';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import ButtonScogoOrange from '../../common/Buttons/ButtonScogoOrange';
import { v4 as uuidv4 } from 'uuid';
import { sendToastMessage } from '../../actions/toast';
import Teams from '../Inputs/Teams';
import Users from '../Inputs/Users';
import { useNavigate } from 'react-router-dom';
import Radio from '../../common/Form/Radio';
import { convertIntoPaisa, isValidEmail } from '../../utils/utils';
import { SkeltonCardTwo } from '../../common/SkeltonCardTwo';
import { getTeamsDropDownList, getTeamUsersList } from '../../actions/teams';
import Select from '../../common/Form/Select';

const quotesArrayName = 'quotes';

export const expenseTypes = [
    { key: 'capex', value: 'CAPEX', label: 'Capex' },
    { key: 'opex', value: 'OPEX', label: 'Opex' },
];

const formName = 'raisePo';

export const RaisePOForm = ({ closeModal, ticketId }) => {
    const submitRef = useRef();
    const dispatch = useDispatch();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const showCustomerDropdown = canManageOtherOrgs(newLoggedUserData.actions);
    const serviceRequestData = useSelector((state) => state.serviceRequest.serviceRequestData);
    const navigate = useNavigate();
    const [customApprovalRequired, setCustomApprovalRequired] = useState(false);
    const editorRef = useRef(null);

    useEffect(() => {
        if (ticketId && !serviceRequestData[ticketId]) {
            dispatch(getServiceRequestById({ ticketId }));
        }
    }, [serviceRequestData, ticketId, dispatch]);

    const memoizedServiceRequest = useMemo(() => {
        if (serviceRequestData && ticketId && serviceRequestData[ticketId]) {
            return serviceRequestData[ticketId];
        }
        return {};
    }, [serviceRequestData, ticketId]);

    const onSubmit = (formValues) => {
        const payload = {
            org_id: actualValueForDropdown(formValues.customer),
            org_gst_number: formValues.org_gst_number || "",
            org_address: formValues.org_address || "",
            vendor_id: actualValueForDropdown(formValues.vendor),
            vendor_gst_number: formValues.vendor_gst_number || "",
            vendor_address: formValues.vendor_address || "",
            title: formValues.title || "",
            description: formValues.description || "",
            payment_terms: formValues.payment_terms ? parseInt(formValues.payment_terms) : 0,
            location_id: actualValueForDropdown(formValues.location_id),
            external_pr_number: formValues.external_pr_number || "",
            external_po_number: formValues.external_po_number || "",
            billing_address: formValues.billing_address || "",
            shipping_address: formValues.shipping_address || "",
            type: "NEW",
            quote_date: formValues.po_date,
            po_date: formValues.po_date,
            currency: "INR",
            taxes: [{
                amount: 0,
                percentage: 18,
                type: 'IGST' // hardcoded 
            }],
            expense_type: formValues.expense_type,
            line_items: formValues[quotesArrayName]?.map(item => ({
                type: "PO_ITEM",
                category: actualValueForDropdown(item.category) || "",
                item_id: actualValueForDropdown(item.asset_type) || "",
                item_title: item.asset_type?.label || "",
                description: item.description || "",
                quantity: parseInt(item.quantity) || 0,
                unit: "UNIT",
                currency: "INR",
                unit_price: convertIntoPaisa(item.unit_price) || 0,
                start_date: new Date().toISOString(),
                is_recurring: item.is_recurring || false,
                end_date: item.end_date ? new Date(item.end_date).toISOString() : "",
            })) || []
        };

        if (editorRef && editorRef.current && editorRef.current.getContent() !== '') {
            payload.terms = editorRef.current.getContent();
        }

        if (formValues.emails) {
            payload.emails = formValues.emails.split(',').filter(email => isValidEmail(email))
        }

        const approvalRequired = formValues.approval_required;

        if (actualValueForDropdown(approvalRequired) === 'auto') {
            payload.approval_required = false;
        } else if (actualValueForDropdown(approvalRequired) === 'custom' && customApprovalRequired) {
            payload.approval_required = true;
            payload.approvers = customApprovalRequired.map((approver, index) => {
                return {
                    level: index + 1,
                    team_id: approver.team_id,
                    users: approver?.users?.length > 0 ? approver.users : undefined
                };
            });
        }
        else {
            payload.approval_required = true;
            if (approvalRequired && approvalRequired.levels && approvalRequired.levels.length > 0) {
                payload.approvers = approvalRequired.levels.map((level, index) => {
                    return {
                        level: index + 1,
                        team_id: level.team_id,
                        users: level.user_ids
                    };
                });
            }
        }

        dispatch(createPo({ payload, formName, navigate }));
    };


    const defaultValues = {
        expense_type: expenseTypes[0].value,
        // quote_date: new Date(),
        po_date: new Date(),
        [quotesArrayName]: [{ id: `${Math.random()}`, isEditable: true, isDeleteable: false }]
    };

    const methods = useForm({ defaultValues, mode: 'all' });
    const customer = methods.watch('customer');

    const onApprovalRequiredChange = useCallback((e) => {
        const value = actualValueForDropdown(e);
        if (value === 'custom') {
            dispatch(openCustomModal({
                modalComponent: <CustomApprovalModal setCustomApprovalRequired={setCustomApprovalRequired} org_id={actualValueForDropdown(customer)} />,
                heading: 'Custom Approval',
                modalWidth: '80rem',
                modalHeight: 'auto',
            }));
        }
    }, [setCustomApprovalRequired, dispatch, customer]);

    return (
        <Form onSubmit={onSubmit} methods={methods} submitRef={submitRef} className='bg-white w-full pb-2 approvePOContainer rounded-lg shadow-lg'>
            <div className='approvePOForm overflow-y-auto bg-white rounded-t-lg'>
                <RaisePoInputs
                    showCustomerDropdown={showCustomerDropdown}
                    ticketId={ticketId}
                    serviceRequest={memoizedServiceRequest}
                    quotesArrayName={quotesArrayName}
                    onApprovalRequiredChange={onApprovalRequiredChange}
                    editorRef={editorRef}
                />
            </div>
            <div className='approvePOButtons flex gap-2 items-center bg-white px-7 rounded-b-lg bottom-0 sticky'>
                <RaisePOButtons submitRef={submitRef} formName={formName} closeModal={closeModal} />
            </div>
        </Form>
    );
};



const CustomApprovalModal = ({ setCustomApprovalRequired, org_id }) => {
    const approversArrayName = 'approvers';
    const methods = useForm({
        defaultValues: {
            [approversArrayName]: [],
            approval_required: true
        }
    });
    const { control, setValue, handleSubmit } = methods;
    const { fields, append, remove, swap } = useFieldArray({
        control,
        name: approversArrayName,
    });

    const dispatch = useDispatch();

    const onRemove = (field, idx) => {
        if (field.isNew) remove(idx);
        else dispatch(sendToastMessage({ message: `Can't Delete`, status: 'danger' }));
    };

    function onDragEnd(result) {
        if (!result.destination) return;
        let sourceIndex = result.source.index;
        let destinationIndex = result.destination.index;
        swap(sourceIndex, destinationIndex);
    }

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: 'none',
        backgroundColor: isDragging ? '#cae4f6' : 'white',
        ...draggableStyle,
    });

    const onSubmit = (data) => {
        const approvers = data[approversArrayName].map((approver, index) => {
            const isNewApprover = approver.isNew;
            return {
                level: index + 1,
                team_id: isNewApprover ? actualValueForDropdown(approver.team) : approver?.team?.value,
                users: isNewApprover ? approver.users.map(user => user.value) : (approver.users.map(user => user.value) || [])
            };
        });

        setCustomApprovalRequired(approvers);
        dispatch(closeModalAction());
    };

    return (
        <Form onCancel={() => dispatch(closeModalAction())} submitContainerClassName='flex justify-end gap-2 px-4 pb-3 pt-1' showSubmit methods={methods} onSubmit={handleSubmit(onSubmit)} className='relative bg-white rounded-lg overflow-hidden'>
            {/* Header Section */}
            <div className='bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200'>
                <h3 className='text-lg font-semibold text-gray-800 flex items-center'>
                    <span className="material-icons text-scogoprimary mr-2">group</span>
                    Approval Workflow
                </h3>
                <p className='text-gray-600 text-sm mt-1'>Drag and drop to reorder approvers. The approval will follow this sequence.</p>
            </div>

            {/* Content Section */}
            <div className='p-6'>
                {/* Info Banner */}
                <div className='mb-4'>
                    <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                        <div className='flex items-center'>
                            <span className="material-icons text-blue-600 mr-2 text-sm">info</span>
                            <p className='text-blue-800 font-medium text-xs'>Drag & drop rows to reorder approval priorities</p>
                        </div>
                    </div>
                </div>

                {/* Approvers Table */}
                <div className='relative bg-white rounded-lg shadow border border-gray-200 overflow-hidden flex flex-col max-h-80'>
                    {/* Table Header */}
                    <div className='bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex-shrink-0'>
                        <div className='flex gap-3 w-full py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                            <div className='w-2/12 flex items-center'>
                                <span className="material-icons text-gray-500 mr-1 text-sm">drag_indicator</span>
                                Priority
                            </div>
                            <div className='w-4/12 flex items-center'>
                                <span className="material-icons text-gray-500 mr-1 text-sm">group</span>
                                Team
                            </div>
                            <div className='w-4/12 flex items-center'>
                                <span className="material-icons text-gray-500 mr-1 text-sm">person</span>
                                Users
                            </div>
                            <div className='w-2/12 flex items-center'>
                                <span className="material-icons text-gray-500 mr-1 text-sm">settings</span>
                                Actions
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className='flex-1 overflow-y-auto'>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId='item_list'>
                                {(provided) => (
                                    <ul
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className='divide-y divide-gray-100'
                                    >

                                        {fields.map((field, index) => (
                                            <Draggable key={field.id} draggableId={`item_list_${field.id}`} index={index}>
                                                {(provided, snapshot) => (
                                                    <li
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                                        className='transition-colors duration-200'
                                                    >
                                                        <ApproverItem
                                                            approversArrayName={approversArrayName}
                                                            field={field}
                                                            index={index}
                                                            onRemove={onRemove}
                                                            org_id={org_id}
                                                        />
                                                    </li>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </ul>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>

                    {/* Add Button */}
                    <div className='px-3 py-2 bg-gray-50 border-t border-gray-200 flex-shrink-0'>
                        <button
                            type="button"
                            onClick={() => {
                                append({ id: uuidv4(), isNew: true, users: [], team: null });
                            }}
                            style={{
                                fontSize: '0.9rem'
                            }}
                            className="group flex items-center justify-center px-4 py-2 text-sm font-semibold text-scogoprimary bg-gradient-to-r from-white to-gray-50 border-2 border-scogoprimary rounded-lg shadow hover:shadow-lg"
                        >
                            <span className="material-icons text-sm mr-2">add_circle</span>
                            Add New
                        </button>
                    </div>
                </div>

            </div>
        </Form>
    );
};

const ApproverItem = ({ field, index, approversArrayName, onRemove, org_id }) => {
    const { watch, setValue } = useFormContext();

    const team = watch(`${approversArrayName}[${index}].team`);
    const users = watch(`${approversArrayName}[${index}].users`);

    useEffect(() => {
        if (team) {
            setValue(`${approversArrayName}[${index}].users`, []);
        }
    }, [team, setValue, approversArrayName, index]);

    useEffect(() => {
        if (users && users.length > 0) {
            setValue(`${approversArrayName}[${index}].team`, null);
        }
    }, [users, setValue, approversArrayName, index]);

    const getTeamColumn = ({ isNew }, idx) => {
        if (!isNew && field?.teamName) {
            return (
                <div className='flex flex-wrap gap-1'>
                    <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-800 border border-blue-200'>
                        <span className="material-icons text-xs mr-1 text-blue-600">group</span>
                        {validateStringValue(field?.teamName)}
                    </span>
                </div>
            );
        }

        if (isNew) {
            return (
                <RenderTeamInput
                    name={`${approversArrayName}[${idx}].team`}
                    org_id={org_id}
                    isDisabled={users && users.length > 0}
                />
            );
        }

        return <></>;
    };

    const getUsersColumn = ({ isNew }, idx) => {
        if (!isNew && Array.isArray(field?.users) && field.users.length > 0) {
            return (
                <div className='flex flex-wrap gap-1'>
                    {field.users.map((user, userIdx) => (
                        <span
                            key={userIdx}
                            className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 border border-green-200'
                        >
                            <span className="material-icons text-xs mr-1 text-green-600">person</span>
                            {validateStringValue(user.label)}
                        </span>
                    ))}
                </div>
            );
        }

        if (isNew) {
            return (
                <RenderUserInput
                    name={`${approversArrayName}[${idx}].users`}
                    org_id={org_id}
                    isDisabled={!!team}
                />
            );
        }

        return <></>;
    };

    return (
        <div key={field.id} className='group flex items-center gap-3 w-full hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-sm py-3 px-4 transition-all duration-300 hover:shadow-sm'>
            <div className='w-2/12 flex items-center gap-2'>
                <div className='flex items-center justify-center w-6 h-6 bg-gradient-to-r from-scogoprimary to-scogopriamry2 text-gray-900 rounded-full text-xs font-bold shadow'>
                    {index + 1}
                </div>
                <span className='material-icons text-gray-400 text-base cursor-move group-hover:text-scogoprimary transition-colors duration-300'>drag_indicator</span>
            </div>
            <div className='w-4/12'>
                <div className='space-y-1'>
                    {getTeamColumn(field, index)}
                </div>
            </div>
            <div className='w-4/12'>
                <div className='space-y-1'>
                    {getUsersColumn(field, index)}
                </div>
            </div>
            <div className='w-2/12 flex items-center gap-2'>
                <button
                    type="button"
                    onClick={() => onRemove(field, index)}
                    className="group flex items-center justify-center w-8 h-8 text-red-600 bg-red-50 rounded hover:bg-red-100 transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-red-200"
                    disabled={!field.isNew}
                    title={field.isNew ? 'Delete Approver' : 'Cannot delete existing approver'}
                >
                    <span className="material-icons text-base group-hover:animate-pulse">delete</span>
                </button>
            </div>
        </div>
    );
};



export const RenderTeamInput = ({ name, isDisabled, org_id }) => {
    const dispatch = useDispatch();
    const teamsDropDownList = useSelector((state) => state.teams.teamsDropDownList);
    const isTeamsDropDownListLoading = useSelector((state) => state.loading.isTeamsDropDownListLoading);

    const memoizedTeamsDropDownList = useMemo(() => {
        const teamsOptions = teamsDropDownList || [];
        return teamsOptions;
    }, [teamsDropDownList]);

    const getTeams = ({ limit = 100, page = 1, query = '', org_id }) => {
        dispatch(getTeamsDropDownList({ limit, page, query, org_id }));
    };

    const debouncedTeamsSearchSearch = useCallback(debounce(getTeams), []);

    useEffect(() => {
        if (memoizedTeamsDropDownList.length === 0 && org_id) {
            getTeams({ org_id });
        }
    }, [org_id, memoizedTeamsDropDownList]);

    return (
        <Select
            className='w-full focus:border-scogo15'
            label='Team'
            name={name}
            options={memoizedTeamsDropDownList}
            isLoading={isTeamsDropDownListLoading}
            onInputChange={(event) => {
                if (event.length > 0) {
                    let p = { query: event };
                    if (org_id) p.org_id = org_id;
                    debouncedTeamsSearchSearch(p);
                }
            }}
            isMulti={false}
            readOnly9={isDisabled}
        />
    );
};


export const RenderUserInput = ({ name, isDisabled, org_id }) => {
    const dispatch = useDispatch();
    const teamUsersList = useSelector((state) => state.teams.teamUsersList);
    const isUsersDropDownLoading = useSelector((state) => state.loading.isUsersDropDownLoading);

    const memoizedUsersList = useMemo(() => {
        let usersOptions = teamUsersList || [];
        return usersOptions;
    }, [teamUsersList]);

    const getUsers = ({ limit = 100, page = 1, query = '', org_id }) => {
        dispatch(getTeamUsersList({ limit, page, org_id, query }));
    };

    const debouncedTeamsSearchSearch = useCallback(debounce(getUsers), []);


    useEffect(() => {
        if (memoizedUsersList.length === 0 && org_id) {
            getUsers({ org_id });
        }
    }, [org_id, memoizedUsersList]);

    return (
        <Select
            className='w-full focus:border-scogo15'
            label='Users'
            name={name}
            options={memoizedUsersList}
            isLoading={isUsersDropDownLoading}
            onInputChange={(event) => {
                if (event.length > 0) {
                    let p = { query: event };
                    if (org_id) p.org_id = org_id;
                    debouncedTeamsSearchSearch(p)
                }
            }}
            isMulti={true}
            readOnly={isDisabled}
        />
    );
};